import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useUpcomingAnniversaries } from '@/hooks/use-api'
import { Award } from 'lucide-react'
import { CATEGORICAL } from '@/lib/theme'

function getInitials(first?: string | null, last?: string | null): string {
  return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?'
}

export function AnniversariesWidget() {
  const { data, isLoading, error } = useUpcomingAnniversaries()
  const items = data ?? []

  if (error) return null
  if (!isLoading && items.length === 0) return null

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Award className="h-4 w-4" style={{ color: CATEGORICAL.clay.solid }} aria-hidden="true" />
          Work Anniversaries This Month
        </CardTitle>
        <CardDescription>
          {items.length} {items.length === 1 ? 'person' : 'people'} celebrating
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p role="status" className="text-sm text-muted-foreground">
            Loading…
          </p>
        ) : (
          <ul className="space-y-2" aria-label="Work anniversaries">
            {items.map((a) => (
              <li key={a.id}>
                <Link
                  to={`/hr/employees/${a.id}`}
                  className="flex items-center gap-3 p-2 rounded hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {a.profile_photo_url ? (
                    <img
                      src={a.profile_photo_url}
                      alt=""
                      className="h-8 w-8 rounded-full object-cover bg-muted"
                    />
                  ) : (
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold"
                      style={{
                        backgroundColor: CATEGORICAL.clay.tint,
                        color: CATEGORICAL.prussian.solid,
                      }}
                      aria-hidden="true"
                    >
                      {getInitials(a.first_name, a.last_name)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {a.preferred_name || a.first_name} {a.last_name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {a.years_of_service} {a.years_of_service === 1 ? 'year' : 'years'}
                      {a.job_title ? ` · ${a.job_title}` : ''}
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
